"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Leaf, Wind, Droplets, ArrowRight, Activity, Factory, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const solutions = [{
  id: 1,
  title: "Supressão no ponto de emissão",
  description: "Gotículas micrométricas que capturam partículas no momento exato da transferência de material, britagem ou descarregamento.",
  tag: "Alta Eficiência",
  iconName: "Wind"
}, {
  id: 2,
  title: "Nebulização e cortinas de retenção",
  description: "Sistemas de névoa fina projetados para criar barreiras ativas em áreas abertas, pilhas de armazenamento e armazéns.",
  tag: "Áreas Abertas",
  iconName: "Droplets"
}, {
  id: 3,
  title: "Humidificação controlada de sólidos",
  description: "Aplicação precisa de humidade em esteiras transportadoras e misturadores, prevenindo poeiras sem ensopar o produto.",
  tag: "Zero Desperdício",
  iconName: "Factory"
}];
const benefits = [{
  id: 1,
  category: "Segurança no trabalho",
  points: ["Redução drástica do risco de inalação de partículas respiráveis (PM10 e PM2.5).", "Minimização substancial do perigo de explosões por poeiras combustíveis.", "Melhoria imediata da visibilidade em zonas de manobra e operação de máquinas."],
  iconName: "ShieldCheck"
}, {
  id: 2,
  category: "Sustentabilidade e conformidade",
  points: ["Cumprimento integral dos limites legais de emissões e normas ambientais.", "Consumo otimizado de água com bicos de pulverização de alta tecnologia.", "Proteção do solo e das linhas de água locais contra lixiviação excessiva."],
  iconName: "Leaf"
}];
export default function DustControl() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: string) => {
    switch (name) {
      case "Wind":
        return <Wind className="size-6 text-secondary" />;
      case "Droplets":
        return <Droplets className="size-6 text-secondary" />;
      case "Factory":
        return <Factory className="size-6 text-secondary" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-7 text-primary" />;
      case "Leaf":
        return <Leaf className="size-7 text-primary" />;
      default:
        return <Activity className="size-6 text-secondary" />;
    }
  };
  return <section id="dustcontrolsolutionsoverview" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="outline" className="mb-4 text-xs font-semibold px-3 py-1 bg-accent text-accent-foreground border-border">
            Soluções de Engenharia
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Controlo eficaz de poeiras industriais
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Elimine partículas suspensas com recurso a sistemas de pulverização de precisão da Spraying Systems Co., garantindo ambientes seguros e processos sustentáveis.
          </p>
        </div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-20">
          {solutions.map((item, i) => <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? undefined : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                <CardHeader>
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-lg bg-accent flex items-center justify-center">
                      {getIcon(item.iconName)}
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {item.tag}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-bold text-foreground">
                    {item.title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                    {item.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="w-full border-t border-border pt-4">
                    <Link to="/TechnicalSolutionsPage" className="inline-flex items-center gap-1.5 text-sm font-semibold text-secondary hover:text-secondary/80 transition-colors">
                      Saber mais sobre aplicação
                      <ArrowRight className="size-4" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {/* Benefits Section */}
        <div className="rounded-2xl bg-muted/40 border border-border p-6 sm:p-10 lg:p-12 mb-16">
          <div className="max-w-2xl mb-8">
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground mb-3">
              Impacto ambiental e segurança operacional
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              A redução de poeiras não é apenas uma obrigação legal, é uma salvaguarda para as equipas e um compromisso com a eficiência energética e hídrica.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {benefits.map((benefit, i) => <motion.div key={benefit.id} data-index={i} initial={shouldReduceMotion ? undefined : {
            opacity: 0,
            x: i % 2 === 0 ? -15 : 15
          }} whileInView={shouldReduceMotion ? undefined : {
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-card border border-border rounded-xl p-6 sm:p-8 shadow-xs flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2.5 rounded-md bg-primary/10">
                      {getIcon(benefit.iconName)}
                    </div>
                    <h4 className="text-lg sm:text-xl font-semibold text-foreground">
                      {benefit.category}
                    </h4>
                  </div>
                  <ul className="space-y-3.5">
                    {benefit.points.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-start gap-3 text-sm sm:text-base text-muted-foreground">
                        <CheckCircle2 className="size-5 text-secondary shrink-0 mt-0.5" />
                        <span>{point}</span>
                      </li>)}
                  </ul>
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* Visual Support & CTA Strip */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center rounded-2xl bg-primary text-primary-foreground p-8 sm:p-12">
          <div className="lg:col-span-8">
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">
              Precisa de um diagnóstico técnico para o seu espaço fabril?
            </h3>
            <p className="text-primary-foreground/80 text-sm sm:text-base max-w-2xl leading-relaxed">
              Os especialistas da Eurocontrol analisam a dimensão das partículas, as correntes de ar e as taxas de fluxo para implementar a solução exata.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-end items-start lg:items-end">
            <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto shadow-md">
              <Link to="/ContactsPage">
                Pedir estudo técnico
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto bg-transparent border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
              <Link to="/ApplicationsPage">
                Ver todas as aplicações
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}